import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ConfigService } from '../config/config.service';
import { LoggingService } from '../shared/logging/logging.service';
import {
  WebhookSubscription,
  CreateWebhookSubscriptionRequest,
  UpdateWebhookSubscriptionRequest,
  TestWebhookResult,
  WebhookTypeConfiguration,
} from '../../models/webhook.models';

/**
 * Service for managing webhook subscriptions via the vNext Modular Monolith API.
 * Uses the standard HttpClient (with Keycloak auth interceptors).
 */
@Injectable({ providedIn: 'root' })
export class WebhookService {
  private httpClient = inject(HttpClient);
  private configService = inject(ConfigService);
  private loggingService = inject(LoggingService);

  private get basePath(): string {
    return this.configService.config?.apiBasePath ?? 'http://localhost:5000';
  }

  private get baseUrl(): string {
    return `${this.basePath}/webhooks/subscriptions`;
  }

  private get typesUrl(): string {
    return `${this.basePath}/webhooks/types`;
  }

  // --- Queries ---

  /**
   * Get all webhook subscriptions
   */
  getAll(): Observable<WebhookSubscription[]> {
    this.loggingService.debug('WebhookService', 'Fetching all webhook subscriptions');
    return this.httpClient.get<WebhookSubscription[]>(this.baseUrl);
  }

  /**
   * Get a single webhook subscription by ID
   */
  getById(id: string): Observable<WebhookSubscription> {
    this.loggingService.debug('WebhookService', `Fetching webhook subscription ${id}`);
    return this.httpClient.get<WebhookSubscription>(`${this.baseUrl}/${id}`);
  }

  // --- Commands ---

  /**
   * Create a new webhook subscription
   * @returns The new subscription ID
   */
  create(request: CreateWebhookSubscriptionRequest): Observable<string> {
    this.loggingService.info('WebhookService', 'Creating webhook subscription', {
      clientName: request.clientName,
      webhookType: request.webhookType,
    });
    return this.httpClient.post(this.baseUrl, request, { responseType: 'text' });
  }

  /**
   * Update an existing webhook subscription
   */
  update(id: string, request: UpdateWebhookSubscriptionRequest): Observable<void> {
    this.loggingService.info('WebhookService', `Updating webhook subscription ${id}`);
    return this.httpClient.put<void>(`${this.baseUrl}/${id}`, request);
  }

  /**
   * Delete a webhook subscription
   */
  delete(id: string): Observable<void> {
    this.loggingService.info('WebhookService', `Deleting webhook subscription ${id}`);
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }

  /**
   * Send a test payload to a webhook subscription's endpoint
   */
  test(id: string): Observable<TestWebhookResult> {
    this.loggingService.info('WebhookService', `Testing webhook subscription ${id}`);
    return this.httpClient.post<TestWebhookResult>(`${this.baseUrl}/${id}/test`, null);
  }

  // --- Type Configurations ---

  /**
   * Get all webhook type configurations (enabled/disabled status)
   */
  getTypeConfigurations(): Observable<WebhookTypeConfiguration[]> {
    this.loggingService.debug('WebhookService', 'Fetching webhook type configurations');
    return this.httpClient.get<WebhookTypeConfiguration[]>(this.typesUrl);
  }

  /**
   * Enable a webhook type
   */
  enableType(type: number): Observable<void> {
    this.loggingService.info('WebhookService', `Enabling webhook type ${type}`);
    return this.httpClient.put<void>(`${this.typesUrl}/${type}/enable`, null);
  }

  /**
   * Disable a webhook type
   */
  disableType(type: number): Observable<void> {
    this.loggingService.info('WebhookService', `Disabling webhook type ${type}`);
    return this.httpClient.put<void>(`${this.typesUrl}/${type}/disable`, null);
  }
}
